import React, { useState } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { useAuth } from '../../../context/AuthContext';
import { Button, IconButton } from '../../../design-system/Button';
import { Input } from '../../../design-system/Input';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Calendar as CalendarIcon
} from 'lucide-react';

export const CalendarView = () => {
  const { user } = useAuth();
  const { tasks, setSelectedTask, setIsQuickAddOpen, updateTask, addTask } = useWorkspace();
  const [currentMonthIndex, setCurrentMonthIndex] = useState(7); // August (0-indexed)
  const [currentYear, setCurrentYear] = useState(2026);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week'
  const [search, setSearch] = useState('');
  const [dragOverDate, setDragOverDate] = useState(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate days for the month
  const firstDayIndex = new Date(currentYear, currentMonthIndex, 1).getDay();
  const totalDaysInMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate();

  const calendarCells = [];
  for (let i = 0; i < firstDayIndex; i++) {
    calendarCells.push(null);
  }
  for (let d = 1; d <= totalDaysInMonth; d++) {
    calendarCells.push(d);
  }

  // Week view calculations (Week containing Aug 22, 2026)
  const weekDays = [16, 17, 18, 19, 20, 21, 22]; // Aug 16-22

  const handlePrev = () => {
    if (currentMonthIndex === 0) {
      setCurrentMonthIndex(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonthIndex(currentMonthIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentMonthIndex === 11) {
      setCurrentMonthIndex(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonthIndex(currentMonthIndex + 1);
    }
  };

  const handleToday = () => {
    setCurrentMonthIndex(7);
    setCurrentYear(2026);
  };

  // Drag-to-reschedule
  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('text/plain', taskId);
  };

  const handleDragOver = (e, dateStr) => {
    e.preventDefault();
    if (dragOverDate !== dateStr) setDragOverDate(dateStr);
  };

  const handleDrop = (e, targetDateStr) => {
    e.preventDefault();
    setDragOverDate(null);
    const taskId = e.dataTransfer.getData('text/plain');
    if (taskId) {
      updateTask(taskId, { dueDate: targetDateStr });
    }
  };

  const handleQuickAddForDate = (dateStr) => {
    const title = prompt(`Enter task title scheduled for ${dateStr}:`);
    if (title && title.trim()) {
      const currentUserName = user?.name || 'Workspace Owner';
      const currentUserAvatar = user?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(currentUserName)}`;

      addTask({
        title: title.trim(),
        dueDate: dateStr,
        status: 'todo',
        priority: 'medium',
        assignee: { name: currentUserName, avatar: currentUserAvatar }
      });
    }
  };

  const filteredTasks = tasks.filter((t) =>
    t.title.toLowerCase().includes(search.toLowerCase()) ||
    t.id.toLowerCase().includes(search.toLowerCase())
  );

  const getPriorityColor = (priority, status) => {
    if (status === 'done') return 'var(--color-success)';
    if (priority === 'urgent') return 'var(--color-danger)';
    if (priority === 'high') return 'var(--color-warning)';
    if (priority === 'medium') return 'var(--accent-teal)';
    return 'var(--text-muted)';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} className="animate-fade-in">
      {/* Calendar Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CalendarIcon size={18} color="var(--accent-teal)" />
            <h2 style={{ fontSize: '18px', fontWeight: '800', minWidth: '160px' }}>
              {monthNames[currentMonthIndex]} {currentYear}
            </h2>
          </div>

          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <IconButton icon={<ChevronLeft size={16} />} size="sm" onClick={handlePrev} />
            <Button variant="secondary" size="xs" onClick={handleToday}>
              Today
            </Button>
            <IconButton icon={<ChevronRight size={16} />} size="sm" onClick={handleNext} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ maxWidth: '220px' }}>
            <Input
              placeholder="Search schedule..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search size={14} />}
            />
          </div>

          {/* View Mode Toggle */}
          <div style={{ display: 'flex', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
            <button
              onClick={() => setViewMode('month')}
              style={{
                padding: '5px 12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: viewMode === 'month' ? 'var(--surface-card)' : 'transparent',
                boxShadow: viewMode === 'month' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: viewMode === 'month' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('week')}
              style={{
                padding: '5px 12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: viewMode === 'week' ? 'var(--surface-card)' : 'transparent',
                boxShadow: viewMode === 'week' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: viewMode === 'week' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Week
            </button>
          </div>

          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={15} />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            Schedule Task
          </Button>
        </div>
      </div>

      {/* Calendar Grid Container (Fixed Size & Responsive Scroll Wrapper) */}
      <div
        style={{
          backgroundColor: 'var(--surface-card)',
          boxShadow: 'var(--shadow-raised)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-card)',
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%'
        }}
      >
        <div style={{ minWidth: '720px', display: 'flex', flexDirection: 'column' }}>
          {/* Day of Week Headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              borderBottom: '1px solid var(--border-divider)',
              backgroundColor: 'var(--surface-alt)',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: '700',
              color: 'var(--text-muted)',
              padding: '10px 0',
              userSelect: 'none'
            }}
          >
            {daysOfWeek.map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>

          {/* MONTH VIEW GRID: Strictly Fixed 120px Cell Height with Internal Scroll */}
          {viewMode === 'month' ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gridAutoRows: '120px'
              }}
            >
              {calendarCells.map((day, idx) => {
                if (!day) {
                  return (
                    <div
                      key={`empty_${idx}`}
                      style={{
                        height: '120px',
                        backgroundColor: 'var(--surface-recessed)',
                        borderRight: '1px solid var(--border-divider)',
                        borderBottom: '1px solid var(--border-divider)',
                        opacity: 0.3
                      }}
                    />
                  );
                }

                const dayString = `${currentYear}-${String(currentMonthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayTasks = filteredTasks.filter((t) => t.dueDate === dayString);
                const isToday = currentYear === 2026 && currentMonthIndex === 7 && day === 22;
                const isDragOver = dragOverDate === dayString;

                return (
                  <div
                    key={day}
                    onDragOver={(e) => handleDragOver(e, dayString)}
                    onDragLeave={() => setDragOverDate(null)}
                    onDrop={(e) => handleDrop(e, dayString)}
                    style={{
                      height: '120px',
                      maxHeight: '120px',
                      boxSizing: 'border-box',
                      borderRight: '1px solid var(--border-divider)',
                      borderBottom: '1px solid var(--border-divider)',
                      padding: '6px 8px',
                      backgroundColor: isDragOver
                        ? 'rgba(35, 190, 179, 0.12)'
                        : isToday
                        ? 'rgba(35, 190, 179, 0.05)'
                        : 'transparent',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'background 0.15s'
                    }}
                  >
                    {/* Date Header Number */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, marginBottom: '4px' }}>
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: isToday ? '800' : '600',
                          color: isToday ? 'var(--accent-teal)' : 'var(--text-secondary)',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '999px',
                          backgroundColor: isToday ? 'rgba(35, 190, 179, 0.2)' : 'transparent'
                        }}
                      >
                        {day}
                      </span>

                      <button
                        onClick={() => handleQuickAddForDate(dayString)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          padding: '2px',
                          opacity: 0.6,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title={`Schedule task on ${dayString}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    {/* Day Task List with Fixed Inner Scroll (Never Stretches Parent) */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px',
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingRight: '2px'
                      }}
                      className="custom-mini-scroll"
                    >
                      {dayTasks.map((t) => (
                        <div
                          key={t.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, t.id)}
                          onClick={() => setSelectedTask(t)}
                          style={{
                            padding: '3px 5px',
                            backgroundColor: 'var(--surface-card)',
                            boxShadow: 'var(--shadow-raised-sm)',
                            borderRadius: '4px',
                            fontSize: '10.5px',
                            fontWeight: '600',
                            color: t.status === 'done' ? 'var(--text-muted)' : 'var(--text-primary)',
                            textDecoration: t.status === 'done' ? 'line-through' : 'none',
                            cursor: 'grab',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            borderLeft: `3px solid ${getPriorityColor(t.priority, t.status)}`,
                            flexShrink: 0
                          }}
                          title={`${t.id}: ${t.title}`}
                        >
                          {t.title}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* WEEK VIEW GRID: Fixed 460px Height with Internal Column Scroll */
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                height: '460px'
              }}
            >
              {weekDays.map((day) => {
                const dayString = `2026-08-${String(day).padStart(2, '0')}`;
                const dayTasks = filteredTasks.filter((t) => t.dueDate === dayString);
                const isToday = day === 22;
                const isDragOver = dragOverDate === dayString;

                return (
                  <div
                    key={day}
                    onDragOver={(e) => handleDragOver(e, dayString)}
                    onDragLeave={() => setDragOverDate(null)}
                    onDrop={(e) => handleDrop(e, dayString)}
                    style={{
                      height: '460px',
                      boxSizing: 'border-box',
                      borderRight: '1px solid var(--border-divider)',
                      padding: '10px',
                      backgroundColor: isDragOver
                        ? 'rgba(35, 190, 179, 0.12)'
                        : isToday
                        ? 'rgba(35, 190, 179, 0.05)'
                        : 'transparent',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '13px', fontWeight: '800', color: isToday ? 'var(--accent-teal)' : 'var(--text-primary)' }}>
                        Aug {day} {isToday && '(Today)'}
                      </span>
                      <button
                        onClick={() => handleQuickAddForDate(dayString)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                        title="Add task"
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        flex: 1,
                        overflowY: 'auto',
                        paddingRight: '2px'
                      }}
                      className="custom-mini-scroll"
                    >
                      {dayTasks.map((t) => (
                        <div
                          key={t.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, t.id)}
                          onClick={() => setSelectedTask(t)}
                          style={{
                            padding: '8px',
                            backgroundColor: 'var(--surface-card)',
                            boxShadow: 'var(--shadow-raised-sm)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '11.5px',
                            fontWeight: '600',
                            borderLeft: `4px solid ${getPriorityColor(t.priority, t.status)}`,
                            cursor: 'grab',
                            flexShrink: 0
                          }}
                        >
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '700' }}>{t.id}</div>
                          <div style={{ color: t.status === 'done' ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>
                            {t.title}
                          </div>
                        </div>
                      ))}

                      {dayTasks.length === 0 && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '30px' }}>
                          No tasks scheduled
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
